import { useParams, Link } from "react-router-dom";
import { Clock, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BLOG_POSTS } from "@/data/blog";
import useSEO from "@/hooks/useSEO";
import ShareButtons from "@/components/ShareButtons";

export default function BlogPost() {
    const { slug } = useParams();
    const post = BLOG_POSTS.find((p) => p.slug === slug);

    useSEO({
        title: post ? `${post.title} · Blog Jérémy Tessier` : "Article introuvable",
        description: post?.excerpt,
        canonical: `https://massoclic.ca/blog/${slug}`,
        ogImage: post?.image,
        jsonLd: post
            ? {
                  "@context": "https://schema.org",
                  "@type": "BlogPosting",
                  headline: post.title,
                  description: post.excerpt,
                  image: post.image,
                  datePublished: post.date,
                  keywords: post.keywords,
                  author: {
                      "@type": "Person",
                      name: "Jérémy Tessier",
                      url: "https://massoclic.ca",
                      jobTitle: "Massothérapeute",
                  },
                  publisher: {
                      "@type": "Organization",
                      name: "Jérémy Tessier — Massothérapie",
                      logo: {
                          "@type": "ImageObject",
                          url: "https://massoclic.ca/icon.svg",
                      },
                  },
                  mainEntityOfPage: `https://massoclic.ca/blog/${slug}`,
              }
            : undefined,
    });

    if (!post) {
        return (
            <main className="pt-32 pb-20 min-h-[60vh]">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <h1 className="font-display text-4xl text-[#2C2A29]">Article introuvable</h1>
                    <Link to="/blog">
                        <Button className="mt-6 rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white">
                            Retour au blog
                        </Button>
                    </Link>
                </div>
            </main>
        );
    }

    return (
        <main data-testid="blog-post" className="pt-32 md:pt-40 pb-20">
            <article className="max-w-3xl mx-auto px-6 md:px-8">
                <Link
                    to="/blog"
                    data-testid="blog-back"
                    className="inline-flex items-center gap-2 text-sm text-[#3D8C95] hover:text-[#2C6D75] mb-8"
                >
                    <ArrowLeft size={16} /> Retour au blog
                </Link>

                <div className="flex items-center gap-2 text-xs text-[#6E6862] mb-4">
                    <Clock size={12} />
                    <span>{post.readTime} min de lecture</span>
                    <span>·</span>
                    <span>
                        {new Date(post.date).toLocaleDateString("fr-CA", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                        })}
                    </span>
                </div>

                <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-[#2C2A29] tracking-tight leading-[1.05]">
                    {post.title}
                </h1>

                <div className="mt-8 aspect-[16/9] rounded-3xl overflow-hidden">
                    <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover"
                    />
                </div>

                <div className="mt-10 space-y-5 text-[#2C2A29]">
                    {post.content.map((block, i) => {
                        if (block.type === "h2") {
                            return (
                                <h2
                                    key={i}
                                    className="font-display text-2xl md:text-3xl text-[#2C2A29] mt-10"
                                >
                                    {block.text}
                                </h2>
                            );
                        }
                        if (block.type === "ul") {
                            return (
                                <ul key={i} className="space-y-2 pl-6 list-disc marker:text-[#E07A5F]">
                                    {block.items.map((it, j) => (
                                        <li key={j} className="text-base leading-relaxed text-[#6E6862]">
                                            {it}
                                        </li>
                                    ))}
                                </ul>
                            );
                        }
                        return (
                            <p key={i} className="text-base md:text-lg leading-relaxed text-[#6E6862]">
                                {block.text}
                            </p>
                        );
                    })}
                </div>

                <div className="mt-14 pt-8 border-t border-[#E8E2D9] flex flex-wrap items-center justify-between gap-6">
                    <ShareButtons
                        url={`https://massoclic.ca/blog/${slug}`}
                        title={post.title}
                    />
                    <Link to="/#reservation">
                        <Button
                            data-testid="blog-cta-book"
                            className="rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white px-8 py-6"
                        >
                            Réserver une séance
                        </Button>
                    </Link>
                </div>
            </article>
        </main>
    );
}
